import { ArrowRight, MapPin, X } from "lucide-react";
import { Dialog, DialogContent, IconButton } from "@mui/material";

const styles = {
  header: {
    padding: "16px 20px",
    borderBottom: "1px solid #dee2e6",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    background: "#f8f9fa",
  },
  pinIconWrap: {
    width: "32px",
    height: "32px",
    background: "#e9ecef",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: "1px",
  },
  eyebrow: {
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#adb5bd",
    marginBottom: "3px",
  },
  addressText: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#1a1a2e",
    lineHeight: "1.5",
  },
  accuracyText: {
    fontSize: "11px",
    color: "#adb5bd",
    marginTop: "2px",
  },
  tagsRow: {
    padding: "10px 20px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
    borderBottom: "1px solid #dee2e6",
    background: "#fff",
  },
  tag: {
    fontSize: "11px",
    fontWeight: 500,
    padding: "3px 10px",
    borderRadius: "4px",
    background: "#e9ecef",
    color: "#495057",
    border: "1px solid #dee2e6",
  },
  tagMuted: {
    fontSize: "11px",
    color: "#adb5bd",
    background: "none",
    border: "1px solid #dee2e6",
    padding: "3px 10px",
    borderRadius: "4px",
  },
  footer: {
    padding: "12px 20px",
    background: "#fff",
    borderTop: "1px solid #dee2e6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  coords: {
    fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
    fontSize: "11px",
    color: "#adb5bd",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  coordDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#2f6fcf",
    flexShrink: 0,
  },
  openBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    textDecoration: "none",
    background: "#2f6fcf",
    color: "white",
    fontSize: "12px",
    fontWeight: 600,
    padding: "8px 16px",
    borderRadius: "6px",
    letterSpacing: "0.2px",
    transition: "background 0.15s",
    border: "none",
    cursor: "pointer",
  },
};

const SlotTag = ({ label }) => (
  <span style={styles.tag}>{label}</span>
);

const LocationMapModal = ({
  open,
  onClose,
  locationTitle,
  latitude,
  longitude,
  address,
  block,
  bay,
  row,
  tier,
  accuracy,
}) => {
  const hasCoords = latitude && longitude;
  const hasSlot = block || bay || row || tier;

  const slotItems = [
    block && `B ${block}`,
    bay   && `Bay ${bay}`,
    row   && `R ${row}`,
    tier  && `T ${tier}`,
  ].filter(Boolean);

  const mapsUrl = hasCoords
    ? `https://www.google.com/maps?q=${latitude},${longitude}&z=18`
    : "#";

  const embedUrl = hasCoords
    ? `https://maps.google.com/maps?q=${latitude},${longitude}&t=m&z=17&output=embed&iwloc=near`
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          border: "1px solid #dee2e6",
        },
      }}
    >
      <DialogContent sx={{ padding: "0 !important" }}>

        {/* ── Header ── */}
        <div style={styles.header}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", flex: 1, minWidth: 0 }}>
            <div style={styles.pinIconWrap}>
              <MapPin size={15} color="#2f6fcf" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.eyebrow}>{locationTitle}</div>
              <div style={styles.addressText}>{address || "—"}</div>
              {accuracy && (
                <div style={styles.accuracyText}>±{accuracy}m GPS accuracy</div>
              )}
            </div>
          </div>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#adb5bd",
              flexShrink: 0,
              width: 28,
              height: 28,
              "&:hover": { background: "#e9ecef", color: "#495057" },
            }}
          >
            <X size={14} />
          </IconButton>
        </div>

        {/* ── Slot Tags ── */}
        {hasSlot && (
          <div style={styles.tagsRow}>
            {slotItems.map((label, i) => (
              <SlotTag key={i} label={label} />
            ))}
          </div>
        )}

        {/* ── Map ── */}
        {embedUrl && (
          <iframe
            loading="lazy"
            title="Container Location Map"
            src={embedUrl}
            style={{ width: "100%", height: "300px", border: "none", display: "block" }}
          />
        )}

        {/* ── Footer ── */}
        <div style={styles.footer}>
          <div style={styles.coords}>
            <div style={styles.coordDot} />
            {hasCoords
              ? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
              : "No coordinates"}
          </div>
          <a
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
            style={styles.openBtn}
            onMouseEnter={e => (e.currentTarget.style.background = "#1a55a8")}
            onMouseLeave={e => (e.currentTarget.style.background = "#2f6fcf")}
          >
            Open in Maps <ArrowRight size={13} />
          </a>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default LocationMapModal;